import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StepHelpers } from '@shared/components/Stepper';
import { Button } from '@shared/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from '@shared/hooks/use-toast';
import { queryClient } from "@shared/api/queries/queryClient";
import { PageHeader } from '@shared/components/PageHeader';
import { CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from '@shared/ui/badge';
import { ValueLabel } from '@features/ax/shared/ValueLabel';
import { useCurrency } from '@shared/selectors/globalSelectors';
import { GenericTable } from '@shared/components/GenericTable';
import { formatMonthDay } from '@shared/utils/dateUtils';
import { useAddRate } from '../../hooks';
import { DiscardDialog } from './DiscardDialog';

const formatKey = (key: string) => {
  return key.replace(/([A-Z])/g, ' $1').trim().toUpperCase();
};

const RenderValue = ({ value }: { value: any }): JSX.Element => {
  if (value === null || value === undefined || value === '') return <span className="text-gray-400">—</span>;
  if (typeof value === 'boolean') return <span>{value ? 'Yes' : 'No'}</span>;
  
  if (typeof value !== 'object') {
    return <span className="font-medium">{value.toString()}</span>;
  }
  
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400">None</span>;
    return (
      <div className="space-y-3 mt-2 w-full">
        {value.map((item, idx) => (
          <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200">
            {typeof item === 'object' ? <RenderValue value={item} /> : <span className="font-medium">{item.toString()}</span>}
          </div>
        ))}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mt-2 w-full">
      {Object.entries(value).map(([k, v]) => (
        <div key={k}>
          <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            {formatKey(k)}
          </span>
          <div className="text-sm text-gray-900">
            <RenderValue value={v} />
          </div>
        </div>
      ))}
    </div>
  );
};

const ExpandableSeasonRow = ({ season, idx, currency }: { season: any, idx: number, currency: string }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const rateType = season.rateType?.toString().toLowerCase();
  
  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden hover:border-blue-300 transition-all shadow-sm">
      <div 
        className="flex flex-wrap items-center justify-between gap-4 p-4 cursor-pointer hover:bg-gray-50/50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center min-w-0">
          <div className="mr-3 text-gray-400">
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
          <h5 className="font-semibold text-gray-900 w-40 shrink-0">
            {season.seasonName || `Season ${idx + 1}`}
          </h5>
          <div className="flex flex-wrap items-center gap-3 ml-2">
            <Badge variant="outline">
              {formatMonthDay(season.from || `${season.fromMonth}-${season.fromDay}`)} →{" "}
              {formatMonthDay(season.to || `${season.toMonth}-${season.toDay}`)}
            </Badge>
            <div className="inline-flex items-center px-2.5 py-0.5 rounded-full border border-blue-200 bg-blue-50 text-xs font-semibold text-blue-700 capitalize">
              {season.rateType || "NA"}
            </div>
          </div>
        </div>
        <div className="text-sm font-medium text-gray-700">
          {rateType === "slab based" || rateType === "4" ? (
            <p>{season.rateConfig?.slabs?.length || 0} Slabs</p>
          ) : rateType === "time of use" || rateType === "5" ? (
            <p>{season.rateConfig?.slots?.length || 0} Time Slots</p>
          ) : rateType === "flat rate" || rateType === "3" ? (
            <p>{currency} {season.rateConfig?.rate || '0.00'}/ {season.rateConfig?.unit || 'kWh'}</p>
          ) : rateType === "fixed" || rateType === "7" ? (
            <p>{currency} {season.rateConfig?.rate || '0.00'}/ {season.rateConfig?.unit || 'kWh'}</p>
          ) : rateType === "demand based" ? (
            <p>Energy {season.rateConfig?.energyRate || '0'} · Demand {season.rateConfig?.demandCharge || '0'}</p>
          ) : null}
        </div>
      </div>
      
      {isExpanded && (
        <div className="p-4 bg-gray-50/50 border-t border-gray-100">
          {renderRateStructure(season.rateType, season, currency)}
        </div>
      )}
    </div>
  );
};

const renderRateStructure = (rateType: string | number, configData: any,currency:string) => {
  if (!rateType || !configData) return <RenderValue value={configData} />;
  
  const rtStr = rateType.toString();

  if (rtStr === "5" || rtStr === "Time of Use") {
     const slots = configData.rateConfig.slots || [];
     if (!slots.length) return <span className="text-sm text-gray-500">No time slots configured.</span>;

     return (
       <div className="space-y-3 mt-2">
         {slots.map((slot: any, idx: number) => {
            const hexColor = slot.color || "#e2e8f0";
            return (
              <div 
                key={idx} 
                className="flex items-center justify-between rounded-xl border p-4 shadow-sm"
                style={{
                  backgroundColor: `${hexColor}0A`, // Very light 4% tint
                  borderColor: `${hexColor}50`, // 30% opacity border
                }}
              >
                 <div className="flex flex-wrap items-center gap-4">
                   <div className="w-3.5 h-3.5 rounded-sm shadow-sm border border-black/5" style={{ backgroundColor: hexColor }} />
                   <span className="font-semibold text-gray-900 text-lg">{slot.slotName || 'NA'}</span>
                   <span className="px-2.5 py-0.5 text-xs font-medium text-gray-600 bg-white rounded border border-gray-200 shadow-sm">
                     {slot.from || '00:00'} → {slot.to || '00:00'}
                   </span>
                 </div>
                 <div className="flex items-baseline gap-1">
                   <span className="font-semibold text-gray-900 text-base">{currency} {slot.rate || '0.00'}</span>
                   <span className="text-xs font-medium text-gray-500">/ {slot.unit || 'kWh'}</span>
                 </div>
              </div>
            );
         })}
       </div>
     );
  }

  if (rtStr === "4" || rtStr === "Slab Based") {
    const slabs = configData.rateConfig.slabs || [];
    if (!slabs.length) return <span className="text-sm text-gray-500">No slabs configured.</span>;

    const columns = [
      { key: "slabName", header: "Slab" },
      { key: "from", header: "From" },
      { key: "to", header: "To" },
      { key: "type", header: "Type" },
      { key: "rate", header: "Rate" },
      { key: "unit", header: "Unit" }
    ];

    const tableData = slabs.map((slab: any, idx: number) => ({
      id: idx,
      slabName: slab.slab || slab.name || `Slab ${idx+1}`,
      from: slab.from ?? slab.min ?? 'NA',
      to: slab.to || slab.max || '∞',
      type: slab.type || slab.rateType || 'NA',
      rate: slab.rate || slab.rateAmount || 'NA',
      unit: slab.unit || 'NA'
    }));

    return (
      <div className="mt-2">
        <GenericTable 
          columns={columns as any} 
          data={tableData} 
          rowKey="id"
        />
      </div>
    );
  }

  if (rtStr === "6" || rtStr === "Seasonal") {
    const seasons = configData.rateConfig?.seasons || [];
    if (!seasons.length) return <span className="text-sm text-gray-500">No seasons configured.</span>;

    return (
      <div className="space-y-4 mt-2">
        {seasons.map((season: any, idx: number) => (
          <ExpandableSeasonRow key={idx} season={season} idx={idx} currency={currency} />
        ))}
      </div>
    );
  }

  if (rtStr === "3" || rtStr === "Flat Rate") {
    return (
      <div className="overflow-hidden bg-white mt-2">
        Flat Rate of {currency} {configData.rateConfig.rate}/ {configData.rateConfig.unit}
      </div>
    );
  }

  if (rtStr === "Demand Based") {
    return (
      <p>
          Energy {configData.rateConfig.energyRate} · Demand{" "}
          {configData.rateConfig.demandCharge}
        </p>
    );
  }

  

  if (rtStr === "7" || rtStr === "Fixed") {
    return (
      <div className="overflow-hidden bg-white mt-2">
        Fixed Rate of {currency} {configData.rateConfig.rate}/ {configData.rateConfig.unit}
      </div>
    );
  }
  
  // Fallback Simple UI
  return <RenderValue value={configData} />;
};

interface ReviewPublishProps {
  remoteUtilityId: number;
  storageKey: string;
  logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  stopTracking?: () => void;
}

export const ReviewPublish: React.FC<ReviewPublishProps> = ({
  remoteUtilityId,
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 0,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
  stopTracking
}) => {
  const navigate = useNavigate();
  const dataLoadedRef = useRef(false);
  const [formData, setFormData] = useState<any>({});
  const currency=useCurrency()
  const {mutateAsync:addRate,isPending}=useAddRate();
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const handleDiscard = () => {
    if (stepHelpers && stepHelpers.resetAllData) {
      stepHelpers.resetAllData();
    } else {
      localStorage.removeItem(storageKey);
    }
    navigate("/plan-tarrif/plan-tarrif-landing?tab=tariff");
  };
  
  // In a real scenario, you'd want to access the stepper data from ALL steps to display them here
  const [allStepsData, setAllStepsData] = useState<any[]>([]);

  console.log("allStepsData", allStepsData);

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data via stepHelpers:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          setFormData(savedStepData);
        }
        
        // Load all previous steps data for review
        const allData = [];
        for (let i = 0; i < currentStepIndex; i++) {
          try {
            allData.push(stepHelpers.getStepData(i));
          } catch (e) {
            allData.push(null);
          }
        }
        setAllStepsData(allData);
        
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData]);

  const buildPayload = () => {
    const basicInfo = allStepsData[0] || {};
    const structureData = allStepsData[1] || {};
    const configData = allStepsData[2] || {};
    
    const rateType = structureData.rateType;
    const rtStr = rateType?.toString();

    const payload: any = {
      rate_name: basicInfo.rateName || "",
      rate_code: basicInfo.rateCode || "",
      description: basicInfo.description || "",
      utility_service: basicInfo.utilityService || "",
      remote_utility_id: remoteUtilityId,
      currency: currency,
      rate_type: typeof rateType === 'number' ? rateType : Number(rateType) || rateType,
      effective_date: basicInfo.effectiveFrom || "",
    };
    if(basicInfo.endDate){
      payload.expiry_date = basicInfo.endDate;
    }

    const rateConfig = configData.rateConfig || {};

    if (rtStr === "3" || rtStr === "Flat Rate") {
      payload.rate = [
        {
          rate: Number(rateConfig.rate) || 0,
          unit: rateConfig.unit || ""
        }
      ];
    } else if (rtStr === "4" || rtStr === "Slab Based") {
      const slabs = rateConfig.slabs || [];
      payload.rate = slabs.map((s: any) => ({
        from: Number(s.from) || 0,
        to: s.to && s.to !== "" && s.to !== "∞" ? Number(s.to) : null,
        mode: s.type || "variable",
        rate: Number(s.rate) || 0,
        unit: s.unit || ""
      }));
      payload.min_charge_enabled = false;
      payload.min_charge_threshold = 0;
      payload.min_charge_rate = 0.00;
    } else if (rtStr === "5" || rtStr === "Time of Use") {
      const slots = rateConfig.slots || [];
      payload.rate = slots.map((s: any) => {
        const parseTimeStr = (t: string) => {
          if (!t) return "00:00";
          if (t.includes(':')) return t;
          return `${String(Number(t) || 0).padStart(2, '0')}:00`;
        };
        return {
          name: s.slotName || "",
          start_time: parseTimeStr(s.from),
          end_time: parseTimeStr(s.to),
          rate: Number(s.rate) || 0,
          unit: s.unit || "",
          color: s.color || ""
        };
      });
    } else if (rtStr === "6" || rtStr === "Seasonal") {
      const seasons = rateConfig.seasons || [];
      payload.rate= seasons.map((s: any) => {
        const seasonRtStr = s.rateType?.toString().toLowerCase();
        let mappedType = 0;
        let structure = [];
        
        if (seasonRtStr === "slab based" || seasonRtStr === "4") {
          mappedType = 4;
          structure = (s.rateConfig?.slabs || []).map((slab: any) => ({
            from: Number(slab.from) || 0,
            to: slab.to && slab.to !== "" && slab.to !== "∞" ? Number(slab.to) : null,
            mode: slab.type || "variable",
            rate: Number(slab.rate) || 0,
            unit: slab.unit || ""
          }));
        } else if (seasonRtStr === "flat rate" || seasonRtStr === "3") {
          mappedType = 3;
          structure = [
            {
              rate: Number(s.rateConfig?.rate) || 0,
              unit: s.rateConfig?.unit || ""
            }
          ];
        } else if (seasonRtStr === "fixed" || seasonRtStr === "7") {
          mappedType = 7;
          structure = [
            {
              fixed: Number(s.rateConfig?.rate) || 0,
              variable: 0,
              unit: s.rateConfig?.unit || ""
            }
          ];
        } else if (seasonRtStr === "time of use" || seasonRtStr === "5") {
          mappedType = 5;
          structure = (s.rateConfig?.slots || []).map((slot: any) => {
            const parseTimeStr = (t: string) => {
              if (!t) return "00:00";
              if (t.includes(':')) return t;
              return `${String(Number(t) || 0).padStart(2, '0')}:00`;
            };
            return {
              name: slot.slotName || "",
              start_time: parseTimeStr(slot.from),
              end_time: parseTimeStr(slot.to),
              rate: Number(slot.rate) || 0,
              unit: slot.unit || "",
              color: slot.color || ""
            };
          });
        }

        return {
          name: s.seasonName || "",
          start_date: s.from || "",
          end_date: s.to || "",
          rate_type: mappedType,
          structure
        };
      });
    } else if (rtStr === "7" || rtStr === "Fixed") {
      payload.rate = {
        fixed: Number(rateConfig.rate) || 0,
        unit: rateConfig.unit || ""
      };
    }

    return payload;
  };

  const handlePublish = () => {
    if (clearValidationError) {
      clearValidationError();
    }
    
    const finalPayload = buildPayload();
    console.log("🚀 Submit Payload:", finalPayload);

    addRate(finalPayload, {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Rate successfully created and published.",
        });

        queryClient.refetchQueries({
          queryKey: ["onboarding", "rateList"],
        });

        if (stepHelpers && stepHelpers.resetAllData) {
          stepHelpers.resetAllData();
        }
        
        navigate('/plan-tarrif/plan-tarrif-landing?tab=tariff');
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create rate",
          variant: "destructive",
        });
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <PageHeader title='Review & Publish' subtitle='Confirm your configuration. You can save a draft, schedule activation, submit for approval, or publish immediately.'/>
        
        <div className="mt-8 space-y-6">
          {/* Basic Info Card */}
          {allStepsData[0] && (
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex justify-between items-start border-b border-gray-100 pb-4 mb-4">
                <div>
                  <span className="block text-xs font-semibold tracking-wider text-gray-500 uppercase mb-1">
                    {allStepsData[0].rateCode || "NA"}
                  </span>
                  <h2 className="text-xl font-semibold text-gray-900 mb-1">
                    {allStepsData[0].rateName || 'NA'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {allStepsData[0].description || 'NA'}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="px-3 py-1 text-[11px] font-bold text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full">
                    {allStepsData[1]?.rateTypeName || allStepsData[1]?.rateType || 'NA'}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <ValueLabel value={allStepsData[0].utilityService || 'NA'} label="Utility" />
                <ValueLabel value={allStepsData[0].effectiveFrom || 'NA'} label="Effective From" />
                <ValueLabel value={allStepsData[0].endDate || 'NA'} label="Effective To" />
              </div>
            </div>
          )}

          {/* Rate Structure Card */}
          {allStepsData[1] && (
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-sm font-bold text-gray-800 mb-4">Rate Structure</h3>
              {renderRateStructure(allStepsData[1].rateType, allStepsData[2],currency)}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={isValidating}
        >
          Previous
        </Button>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowDiscardDialog(true)}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            disabled={isValidating}
            isLoading={isPending}
            className="min-w-[120px]"
          >
            Publish Rate
          </Button>
        </div>
      </div>
      <DiscardDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog} onDiscard={handleDiscard} />
    </div>
  );
};
